import { GatsbyImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import BlogIcon from '../assets/icons/insightBlog.svg';
import PodcastIcon from '../assets/icons/insightPodcast.svg';
import VideoIcon from '../assets/icons/insightVideo.svg';
import WhitepaperIcon from '../assets/icons/insightWhitepaper.svg';
import { Button, Text, colors, media, Heading, InternalLink } from '../ui-components';
import { useDefaultImage } from './insight-cards/insight-card';
import CaseStudy from '../models/CaseStudy/CaseStudy';
import Insight from '../models/Insight/Insight';
import { truncateText } from '../utils/truncate-text';
import POVBlock from '../models/POVBlock/POVBlock';
import renderRichText from '../utils/render-rich-text';
import { gaDownloadEvent } from '../utils/ga-custom-event';

const propTypes = {
  featuredContentRaw: PropTypes.object,
  downloadContent: PropTypes.bool
};

/**
 * TODO: In order to allow Case Studies to be in the featured content card, Case Study must be added to the calling fragment/graphql query
 *  example: in the Service Fragments file, in the query for featuredContent field, a case for '... on ContentfulCaseStudy' must be added.
 */

/**
 * Component that renders a card containing a featured insight or case study
 * @component Featured Content Card
 *
 * @param {object} props
 *
 * @prop {object} featuredContentRaw
 * @prop {boolean} downloadContent
 *
 * @returns {JSX.Element}
 */
const FeaturedContentCard = props => {
  const MAX_TITLE_LENGTH = 75;
  const MAX_SNIPPET_LENGTH = 190;

  const { featuredContentRaw, downloadContent = false } = props;
  const contentType = featuredContentRaw?.internal?.type;
  // The featuredContent can be a Case Study or Insight. Default is Insight, and Case Studies are being displayed like Insights of type Blog.
  const featuredContent =
    contentType === 'ContentfulCaseStudy'
      ? new CaseStudy(featuredContentRaw)
      : contentType === 'ContentfulPointOfViewBlock'
      ? new POVBlock(featuredContentRaw) // Example handling for PointOfViewBlock
      : new Insight(featuredContentRaw);
  const defaultImage = useDefaultImage();

  const downloadInsightFile = () => {
    const attachment = featuredContent.downloadableContent;

    gaDownloadEvent(attachment?.fileName);

    fetch(attachment.url).then(response => {
      response.blob().then(blob => {
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = attachment.fileName;
        a.click();
      });
    });
  };

  /**
   * Renders the icon associated with the Insight type, or if a Case Study, then Blog Icon
   * @returns {JSX.Element}
   */
  const renderIcon = () => {
    switch (featuredContent.type) {
      case 'Whitepaper':
        return <FeaturedContentIcon src={WhitepaperIcon} alt={'Whitepaper Icon'} />;
      case 'Podcast':
        return <FeaturedContentIcon src={PodcastIcon} alt={'Podcast Icon'} />;
      case 'Video':
        return <FeaturedContentIcon src={VideoIcon} alt={'Video Icon'} />;
      default:
        return <FeaturedContentIcon src={BlogIcon} alt={'Blog Icon'} />;
    }
  };

  const featuredImage = () => {
    if (featuredContent?.featuredContentHeroImage?.fluid) {
      return featuredContent.featuredContentHeroImage.fluid;
    } else if (featuredContent.heroImageData?.fluid) {
      return featuredContent.heroImageData?.fluid;
    } else if (featuredContent.image?.fluid) {
      return featuredContent.image?.fluid;
    } else return defaultImage.defaultBG.childImageSharp.gatsbyImageData;
  };

  return (
    <FeaturedContentCardContainer id="featuredContent">
      <FeaturedContentImage
        objectPosition={'left'}
        image={featuredImage()}
        alt={
          featuredContent?.featuredContentHeroImage?.title ||
          featuredContent?.heroImageData?.title ||
          featuredContent?.image?.title ||
          'Featured Content Image'
        }
      />
      <FeaturedContentContainer>
        <FeaturedContentTextContainer>
          <FeaturedContentHeaderContainer>
            {renderIcon()}
            <Heading level={5}>
              {truncateText(featuredContent?.featuredContentTitle || featuredContent.title, MAX_TITLE_LENGTH)}
            </Heading>
          </FeaturedContentHeaderContainer>
          <FeaturedContentText>{truncateText((featuredContent.snippet || renderRichText(featuredContent?.bodyRichJSON)[0]?.props?.children[0]), MAX_SNIPPET_LENGTH)}</FeaturedContentText>
        </FeaturedContentTextContainer>
        {contentType === 'ContentfulInsight' && downloadContent && featuredContent.downloadableContent ? (
          <FeaturedContentDownload onClick={() => downloadInsightFile()}>{'Download'}</FeaturedContentDownload>
        ) : (
          <InternalLink
            linkstyle={'primary-button'}
            to={
              contentType === 'ContentfulCaseStudy'
                ? `/case-studies/${featuredContent.slug}`
                : contentType === 'ContentfulPointOfViewBlock'
                ? `${featuredContent?.ctaUrl}`
                : `/insights/${featuredContent.slug}`
            }
          >
            {featuredContent.type === 'Podcast'
              ? 'Listen Now'
              : featuredContent.type === 'Video'
              ? 'Watch Now'
              : contentType === 'ContentfulPointOfViewBlock'
              ? featuredContent?.ctaText || 'Explore Now'
              : 'Read Now'}
          </InternalLink>
        )}
      </FeaturedContentContainer>
    </FeaturedContentCardContainer>
  );
};

const FeaturedContentCardContainer = styled.div`
  align-items: flex-end;
  display: flex;
  max-width: 1024px;
  margin: auto;
  padding: 0;
  z-index: 1;

  @media screen and (max-width: ${media.largeBottom}) {
    padding: 0 20px;
    width: 100%;
  }

  @media screen and (max-width: ${media.mediumTop}) {
    margin: auto;
  }
`;

const FeaturedContentImage = styled(GatsbyImage)`
  aspect-ratio: 400 / 320;
  max-width: 400;
  max-height: 320;
  z-index: 1;
  box-shadow: 0 0 80px 0 rgba(0, 0, 0, 0.06);

  @media screen and (max-width: ${media.largeBottom}) {
    display: none;
  }
`;

const FeaturedContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.pureWhite};
  position: relative;
  margin-left: -24px;
  max-width: 648px;
  padding: 40px 80px 40px 40px;
  box-shadow: 0 0 80px 0 rgba(0, 0, 0, 0.06);
  z-index: 2;
  flex-shrink: 0;
  align-items: flex-start;
  gap: 20px;

  @media screen and (max-width: ${media.largeBottom}) {
    flex: 1 0 0;
    margin: 0;
    max-width: 100%;
    align-items: center;
    align-self: stretch;
    padding: 40px 16px;
    background: ${colors.secondaryBackground};
  }
`;

const FeaturedContentTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  padding: 0;

  @media screen and (max-width: ${media.largeBottom}) {
    gap: 20px;
  }
`;

const FeaturedContentHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 12px;
  max-width: 528px;

  @media screen and (max-width: ${media.largeBottom}) {
    flex-direction: column;
    max-width: 100%;
    text-align: center;
  }
`;

const FeaturedContentIcon = styled.img`
  width: 60px;
  height: 60px;
  margin: 0;
`;

const FeaturedContentText = styled(Text)`
  max-width: 497px;
  font-family: 'Lato', sans-serif;

  @media screen and (max-width: ${media.largeBottom}) {
    max-width: 100%;
    text-align: center;
  }
`;

const FeaturedContentDownload = styled(Button)`
  margin: 0;
  padding: 16px 20px;
`;

FeaturedContentCard.propTypes = propTypes;

export default FeaturedContentCard;
